type Dict = Record<string, unknown>

const DOC_TYPE_LABEL: Record<string, string> = {
  receipt: 'Кассовый чек',
  receiptCorrection: 'Кассовый чек коррекции',
  bso: 'БСО',
  bsoCorrection: 'БСО коррекции',
  openShift: 'Отчет об открытии смены',
  closeShift: 'Отчет о закрытии смены',
  currentStateReport: 'Отчет о текущем состоянии расчетов',
  fiscalReport: 'Отчет о регистрации',
  fiscalReportCorrection: 'Отчет об изменении параметров регистрации',
  closeArchive: 'Отчет о закрытии ФН',
}

const OPERATION: Record<number, string> = {
  1: 'Приход',
  2: 'Возврат прихода',
  3: 'Расход',
  4: 'Возврат расхода',
}

const PAYMENT_TYPE: Record<number, string> = {
  1: 'Предоплата 100%',
  2: 'Предоплата',
  3: 'Аванс',
  4: 'Полный расчет',
  5: 'Частичный расчет и кредит',
  6: 'Передача в кредит',
  7: 'Оплата кредита',
}

const PRODUCT_TYPE: Record<number, string> = {
  1: 'Товар',
  2: 'Подакцизный товар',
  3: 'Работа',
  4: 'Услуга',
  10: 'Платеж',
  11: 'Агентское вознаграждение',
  12: 'Составной предмет расчета',
  13: 'Иной предмет расчета',
}

const TAXATION: Record<number, string> = {
  1: 'ОСН',
  2: 'УСН доход',
  3: 'УСН доход-расход',
  4: 'ЕНВД',
  5: 'ЕСХН',
  6: 'Патент',
}

const NDS_RATE: Record<number, string> = {
  1: 'НДС 20%',
  2: 'НДС 10%',
  3: 'НДС 0%',
  4: 'Без НДС',
  5: 'НДС 20/120',
  6: 'НДС 10/110',
  7: 'НДС 5%',
  8: 'НДС 7%',
  9: 'НДС 22%',
  10: 'НДС 22/122',
  11: 'НДС 5/105',
  12: 'НДС 7/107',
}

const RECEIPT_KEYS = [
  'receipt',
  'receiptCorrection',
  'bso',
  'bsoCorrection',
  'openShift',
  'closeShift',
  'currentStateReport',
  'fiscalReport',
  'fiscalReportCorrection',
  'closeArchive',
] as const

function asDict(v: unknown): Dict | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Dict) : null
}

function num(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** Суммы ФН — целые копейки. Дробные значения считаем уже рублями. */
export function rubFromKopecks(v: unknown): number | null {
  const n = num(v)
  if (n == null) return null
  if (!Number.isInteger(n)) return n
  return n / 100
}

export function fmtMoney(v: unknown): string {
  const n = rubFromKopecks(v)
  if (n == null) return '—'
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtQty(v: unknown): string {
  const n = num(v)
  if (n == null) return '1'
  return Number.isInteger(n) ? String(n) : String(n).replace('.', ',')
}

export function fmtOfdDateTime(v: unknown): string {
  if (v == null) return ''
  const d = typeof v === 'number' ? new Date(v * 1000) : new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function fmtDateTimeQr(v: unknown): string {
  if (v == null) return ''
  const d = typeof v === 'number' ? new Date(v * 1000) : new Date(String(v))
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${y}${m}${day}T${hh}${mi}`
}

function measureLabel(item: Dict): string {
  const u = item.measureUnit ?? item.unit ?? item.measurementUnit
  if (u == null || u === '' || u === 0 || u === '0') return 'шт'
  if (typeof u === 'string') return u
  return 'шт'
}

function itemNdsLabel(item: Dict): string | null {
  const rate = num(item.nds ?? item.ndsRate)
  if (rate != null && NDS_RATE[rate]) return NDS_RATE[rate]
  for (const [key, label] of [
    ['nds22', 'НДС 22%'],
    ['nds20', 'НДС 20%'],
    ['nds18', 'НДС 18%'],
    ['nds10', 'НДС 10%'],
    ['nds7', 'НДС 7%'],
    ['nds5', 'НДС 5%'],
    ['nds0', 'НДС 0%'],
    ['ndsNo', 'Без НДС'],
  ] as const) {
    if (item[key] != null) return label
  }
  return null
}

function ndsTotals(data: Dict): Array<{ label: string; amount: unknown }> {
  const rows: Array<{ label: string; amount: unknown }> = []
  const map: Array<[string, string]> = [
    ['nds22', 'НДС 22%'],
    ['nds20', 'НДС 20%'],
    ['nds18', 'НДС 18%'],
    ['nds10', 'НДС 10%'],
    ['nds7', 'НДС 7%'],
    ['nds5', 'НДС 5%'],
    ['nds0', 'НДС 0%'],
    ['ndsCalculated22', 'НДС 22/122'],
    ['ndsCalculated20', 'НДС 20/120'],
    ['ndsCalculated18', 'НДС 18/118'],
    ['ndsCalculated10', 'НДС 10/110'],
    ['ndsNo', 'Без НДС'],
  ]
  for (const [key, label] of map) {
    if (data[key] != null && Number(data[key]) !== 0) rows.push({ label, amount: data[key] })
  }
  return rows
}

export function extractFiscalDoc(
  payload: Record<string, unknown> | null | undefined,
): { type: string; data: Dict } | null {
  if (!payload) return null

  for (const key of RECEIPT_KEYS) {
    const inner = asDict(payload[key])
    if (inner) return { type: key, data: inner }
  }

  const wrapped = asDict(payload.doc)
  if (wrapped) {
    for (const key of RECEIPT_KEYS) {
      const inner = asDict(wrapped[key])
      if (inner) return { type: key, data: inner }
    }
  }

  if (payload.fiscalDocumentNumber != null || payload.items != null || payload.totalSum != null) {
    return { type: 'receipt', data: payload }
  }

  return null
}

export interface OfdReceiptLine {
  idx: number
  name: string
  qtyLabel: string
  measure: string
  price: unknown
  sum: unknown
  meta: string
}

export interface OfdReceiptModel {
  type: string
  typeLabel: string
  docNum: string | number | null
  user: string | null
  retailPlace: string | null
  address: string | null
  dateLabel: string
  operationLabel: string | null
  paymentModeLabel: string | null
  operator: string | null
  shiftNumber: string | number | null
  items: OfdReceiptLine[]
  total: unknown
  payments: Array<{ label: string; amount: unknown }>
  ndsRows: Array<{ label: string; amount: unknown }>
  fiscal: Array<{ label: string; value: string }>
  buyerAddress: string | null
  senderAddress: string | null
  qrValue: string | null
}

export function buildOfdReceiptModel(
  payload: Record<string, unknown> | null | undefined,
): OfdReceiptModel | null {
  const extracted = extractFiscalDoc(payload)
  if (!extracted) return null

  const { type, data } = extracted
  const rawItems = Array.isArray(data.items) ? (data.items as Dict[]) : []
  const op = num(data.operationType)
  const paymentMode = num(data.paymentType ?? data.paymentModeType)
  const taxation = num(data.taxationType)
  const docNum =
    data.fiscalDocumentNumber != null
      ? (data.fiscalDocumentNumber as string | number)
      : data.requestNumber != null
        ? (data.requestNumber as string | number)
        : null
  const dateRaw = data.receiveDateTime || data.dateTime || data.sendDateTime
  const total = data.totalSum

  const items: OfdReceiptLine[] = rawItems.map((item, idx) => {
    const qty = item.quantity ?? 1
    const payType = num(item.paymentType)
    const prodType = num(item.productType ?? item.paymentSubjectType)
    const nds = itemNdsLabel(item)
    const meta = [
      nds,
      payType != null ? PAYMENT_TYPE[payType] : null,
      prodType != null ? PRODUCT_TYPE[prodType] : null,
    ]
      .filter(Boolean)
      .join(' · ')
    return {
      idx,
      name: String(item.name || 'Позиция'),
      qtyLabel: fmtQty(qty),
      measure: measureLabel(item),
      price: item.price,
      sum: item.sum,
      meta,
    }
  })

  const payments: Array<{ label: string; amount: unknown }> = []
  if (num(data.cashTotalSum)) payments.push({ label: 'Наличными', amount: data.cashTotalSum })
  if (num(data.ecashTotalSum)) payments.push({ label: 'Безналичными', amount: data.ecashTotalSum })
  if (num(data.prepaidSum)) payments.push({ label: 'Аванс', amount: data.prepaidSum })
  if (num(data.creditSum)) payments.push({ label: 'Кредит', amount: data.creditSum })
  if (num(data.provisionSum)) {
    payments.push({ label: 'Встречным предоставлением', amount: data.provisionSum })
  }

  const fiscal: Array<{ label: string; value: string }> = []
  if (data.kktRegId) fiscal.push({ label: 'РН ККТ', value: String(data.kktRegId) })
  if (data.fiscalDriveNumber) fiscal.push({ label: 'ФН', value: String(data.fiscalDriveNumber) })
  if (data.fiscalSign != null) fiscal.push({ label: 'ФП', value: String(data.fiscalSign) })
  if (docNum != null) fiscal.push({ label: 'ФД', value: String(docNum) })
  if (taxation != null) fiscal.push({ label: 'СНО', value: TAXATION[taxation] || String(taxation) })
  if (data.userInn) fiscal.push({ label: 'ИНН', value: String(data.userInn) })

  const qrParts = [
    `t=${fmtDateTimeQr(dateRaw)}`,
    `s=${rubFromKopecks(total)?.toFixed(2) ?? '0.00'}`,
    `fn=${data.fiscalDriveNumber ?? ''}`,
    `i=${docNum ?? ''}`,
    `fp=${data.fiscalSign ?? ''}`,
    `n=${op ?? 1}`,
  ]
  const qrValue =
    data.fiscalDriveNumber && docNum != null && data.fiscalSign != null
      ? qrParts.join('&')
      : null

  return {
    type,
    typeLabel: DOC_TYPE_LABEL[type] || 'Документ',
    docNum,
    user: data.user != null ? String(data.user) : null,
    retailPlace: data.retailPlace != null ? String(data.retailPlace) : null,
    address:
      data.retailPlaceAddress != null
        ? String(data.retailPlaceAddress)
        : data.retailAddress != null
          ? String(data.retailAddress)
          : null,
    dateLabel: fmtOfdDateTime(dateRaw),
    operationLabel: op != null ? OPERATION[op] || `Операция ${op}` : null,
    paymentModeLabel:
      paymentMode != null ? PAYMENT_TYPE[paymentMode] || `Расчет ${paymentMode}` : null,
    operator: data.operator != null ? String(data.operator) : null,
    shiftNumber:
      data.shiftNumber != null ? (data.shiftNumber as string | number) : null,
    items,
    total,
    payments,
    ndsRows: ndsTotals(data),
    fiscal,
    buyerAddress: data.buyerAddress != null ? String(data.buyerAddress) : null,
    senderAddress: data.senderAddress != null ? String(data.senderAddress) : null,
    qrValue,
  }
}
